import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import AutoCompleteInput from "./AutoCompleteInput";
import { updateAppointment } from "./appointments/appointmentSlice";

const CarPicker = ({
    vehicleId = 0,
    appointment,
    selectedVehicle,
    setSelectedVehicle,
    vehicleReset,
    setVehicleReset,
    deferredServices,
    setDeferredServices,
    setCarErrors,
}) => {
    const [allVehicles, setAllVehicles] = useState([]);
    const [allMakes, setAllMakes] = useState([]);
    const [allModels, setAllModels] = useState([]);
    const [allYears, setAllYears] = useState([]);
    const [make, setMake] = useState("");
    const [model, setModel] = useState("");
    const [year, setYear] = useState("");
    const [declinedMakes, setDeclinedMakes] = useState([]);
    const [declinedModels, setDeclinedModels] = useState([]);
    const [oldestYear, setOldestYear] = useState(null);
    const [errors, setErrors] = useState({ year: "", make: "", model: "" });
    const [newVehicle, setNewVehicle] = useState({
        year: "",
        make: "",
        model: "",
        id: vehicleId,
    });

    const dispatch = useDispatch();

    useEffect(() => {
        fetchVehicles("NewCar");
        fetchVehicles("UsedCar");
    }, []);

    useEffect(() => {
        if (appointment?.shopData?.declinedMake) {
            setDeclinedMakes(
                appointment.shopData.declinedMake
                    .split(",")
                    .map((item) => item.trim())
            );
        }
        if (appointment?.shopData?.declinedModel) {
            setDeclinedModels(
                appointment.shopData.declinedModel
                    .split(",")
                    .map((item) => item.trim())
            );
        }
        if (appointment?.shopData?.oldestYearServicable) {
            setOldestYear(appointment.shopData.oldestYearServicable);
        }
    }, [
        appointment?.shopData?.declinedMake,
        appointment?.shopData?.declinedModel,
        appointment?.shopData?.oldestYearServicable,
    ]);

    useEffect(() => {
        const makes = Array.from(
            new Set(allVehicles.map((vehicle) => vehicle.make))
        );

        const filteredMakes = declinedMakes.length
            ? makes.filter((m) => !declinedMakes.includes(m))
            : makes;

        setAllMakes(filteredMakes);
    }, [allVehicles, declinedMakes]);

    useEffect(() => {
        const models = allVehicles
            .filter((vehicle) => vehicle.make === make)
            .map((vehicle) => vehicle.model);

        const uniqueModels = Array.from(new Set(models));

        const filteredModels = declinedModels.length
            ? uniqueModels.filter((m) => !declinedModels.includes(m))
            : uniqueModels;

        setAllModels(filteredModels);
    }, [make, allVehicles, declinedModels]);

    useEffect(() => {
        const years = allVehicles
            .filter((vehicle) => vehicle.model === model)
            .flatMap((vehicle) => vehicle.years.map((y) => y.year));

        setAllYears(Array.from(new Set(years)));
    }, [model, allVehicles]);

    // useEffect(() => {
    //     if (appointment?.vehicle) {
    //         console.log("did I update?");
    //         if (Object.values(errors).every((error) => error === "")) {
    //             dispatch(
    //                 updateAppointment({ updates: { vehicle: newVehicle } })
    //             );
    //         }
    //     }
    // }, [errors, newVehicle, dispatch]);

    useEffect(() => {
        const updateErrors = {};

        // Check for declined makes and models
        if (
            declinedMakes
                .map((m) => m.toLowerCase())
                .includes(make.toLowerCase())
        ) {
            updateErrors.make = "We do not service your make of vehicle";
        }

        if (
            declinedModels
                .map((m) => m.toLowerCase())
                .includes(model.toLowerCase())
        ) {
            updateErrors.model = "We do not service your model of vehicle";
        }

        // Check the oldest serviceable year, but only set updateErrors.year if selectedVehicle.year doesn't meet criteria
        if (
            appointment?.shopData?.oldestYearServicable &&
            (selectedVehicle?.year < oldestYear ||
                (year !== "" && year < oldestYear))
        ) {
            updateErrors.year = `We only service vehicles manufactured during or after ${appointment.shopData.oldestYearServicable}`;
        }

        setErrors(updateErrors);
        setCarErrors(updateErrors);
    }, [
        selectedVehicle?.make,
        selectedVehicle?.model,
        selectedVehicle?.year,
        year,
        make,
        model,
        // appointment,
        // declinedMakes,
        // declinedModels,
        // oldestYear,
    ]);

    useEffect(() => {
        if (!vehicleReset) {
            handleVehicleReset();
        }

        handleNewVehicleSelections(year, make, model);
    }, [year, make, model]);

    const handleVehicleReset = () => {
        dispatch(
            updateAppointment({
                updates: {
                    vehicle: {
                        year: "",
                        make: "",
                        model: "",
                        deferredServices: [],
                        id: vehicleId,
                    },
                },
            })
        );
        setVehicleReset(true);
        setSelectedVehicle(null);
        setErrors({ year: "", make: "", model: "" });
        setCarErrors({ year: "", make: "", model: "" });
        setDeferredServices([]);
    };

    const handleNewVehicleSelections = (year, make, model) => {
        setNewVehicle((prev) => ({
          // ...prev,
          year: year,
          make: make,
          model: model,
          id: prev.id,
        }));

        dispatch(
            updateAppointment({
                updates: {
                    vehicle: {
                        ...appointment.vehicle,
                        crossRef: [],
                        year: year,
                        make: make,
                        model: model,
                    },
                },
            })
        );
    };

    const fetchVehicles = async (vehicleClass) => {
        const requestOptions = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                operationName: "makemodel",
                variables: {
                    sortBy: "make",
                    vehicleClass: vehicleClass,
                    viewAll: true,
                },
                query: "query makemodel($sortBy: String, $vehicleClass: String, $viewAll: Boolean) { makemodel( sortBy: $sortBy vehicleClass: $vehicleClass viewAll: $viewAll ) { vehicles { make model years { year url __typename } __typename } validStartingLetters __typename }}",
            }),
            redirect: "follow",
        };

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/bookings/vehicleLookup`,
                requestOptions
            );
            const result = await response.json();
            setAllVehicles(result.data.makemodel.vehicles);
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <>
            {Object.values(errors).some((error) => error !== "") && (
                <div className="mx-6 mt-5 mb-5 p-1 bg-red-200 border-solid border-[1px] border-red-500 rounded-md text-red-500">
                    <ul>
                        {Object.entries(errors).map(
                            ([key, value], index) =>
                                value && <li key={index}>{value}</li>
                        )}
                    </ul>
                </div>
            )}
            <div className="grid md:grid-cols-3 my-5 gap-2">
                <div className="md:grid-cols-3 mx-6">
                    <AutoCompleteInput
                        options={allMakes}
                        id="Make"
                        setValue={setMake}
                        appointment={appointment}
                    />
                </div>

                <div className="md:grid-cols-3 mx-6">
                    <AutoCompleteInput
                        options={allModels}
                        id="Model"
                        setValue={setModel}
                        appointment={appointment}
                    />
                </div>

                <div className="md:grid-cols-3 mx-6">
                    <AutoCompleteInput
                        options={allYears}
                        id="Year"
                        setValue={setYear}
                        appointment={appointment}
                    />
                </div>
            </div>
        </>
    );
};

export default CarPicker;
